
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Check, Mail, MapPin, MessageSquare, Phone, Send, User } from 'lucide-react';

// Mock user data - would come from auth context in a real app
const CURRENT_USER = {
  name: 'John Doe',
  email: 'john.doe@example.com',
  role: 'student',
  avatar: null
};

// Form schema
const formSchema = z.object({
  name: z.string().min(2, { message: 'Name must be at least 2 characters.' }),
  email: z.string().email({ message: 'Please enter a valid email address.' }),
  subject: z.string().min(5, { message: 'Subject must be at least 5 characters.' }),
  category: z.string({
    required_error: "Please select a category.",
  }),
  message: z.string().min(10, { message: 'Message must be at least 10 characters.' }),
});

type FormValues = z.infer<typeof formSchema>;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 12,
      stiffness: 100
    }
  }
};

export const ContactUs = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      category: '',
      message: '',
    },
  });

  useEffect(()=>{
    const { emailId } = JSON.parse(localStorage.getItem('user') || 'null') ?? {}; // Update with your actual key  
    if(emailId){
      CURRENT_USER.email = emailId;
    }
  },[])

  // Handle form submission
  const onSubmit = (data: FormValues) => {
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);

      toast({
        title: "Message Sent",
        description: "We've received your message and will respond shortly.",
      });

      form.reset();

      // Reset success state after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);
    }, 1500);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header with quote and user info */}
      <div className="bg-gradient-to-r from-bytesplash-primary to-gray-600 text-white p-6 rounded-xl shadow-md mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            {/* <h1 className="text-2xl font-bold mb-2">Hello, {CURRENT_USER.name}!</h1> */}
            <p className="text-2xl font-bold mb-2"><b>{CURRENT_USER.email}</b></p>
          </div>
          <div className="mt-4 md:mt-0 text-center md:text-right">
            <p className="italic text-blue-100">"The best way to get answers is to ask questions."</p>
            <p className="text-blue-200 mt-1">- Bytesplash Support Team</p>
          </div>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Contact Us</h1>
        <p className="text-bytesplash-gray-500">Reach out to our support team with any questions or feedback</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <motion.div
          className="lg:col-span-2"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {isSuccess ? (
            <motion.div
              className="bg-green-50 border border-green-200 rounded-lg p-8 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 100 }}
            >
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">Message Sent Successfully!</h2>
              <p className="text-green-700 mb-6">We've received your message and will get back to you shortly.</p>
              <Button
                onClick={() => setIsSuccess(false)}
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                Send Another Message
              </Button>
            </motion.div>
          ) : (
            <Card className="shadow-soft">
              <CardHeader className="pb-3">
                <CardTitle>Send Us a Message</CardTitle>
                <CardDescription>Fill out the form below to get in touch with our team</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bytesplash-gray-400" size={16} />
                                  <Input placeholder="Your full name" className="pl-10" {...field} />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bytesplash-gray-400" size={16} />
                                  <Input placeholder="Your email address" className="pl-10" {...field} />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="subject"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subject</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bytesplash-gray-400" size={16} />
                                  <Input placeholder="Subject of your message" className="pl-10" {...field} />
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <FormField
                          control={form.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Category</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a category" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="general">General Inquiry</SelectItem>
                                  <SelectItem value="technical">Technical Support</SelectItem>
                                  <SelectItem value="billing">Billing Issue</SelectItem>
                                  <SelectItem value="feedback">Feedback</SelectItem>
                                  <SelectItem value="courses">Course Related</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    </div>

                    <motion.div variants={itemVariants}>
                      <FormField
                        control={form.control}
                        name="message"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Message</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Please describe your issue or question in detail..."
                                className="min-h-[150px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </motion.div>

                    <motion.div
                      variants={itemVariants}
                      className="flex justify-end"
                    >
                      <Button
                        type="submit"
                        className="bg-bytesplash-primary hover:bg-bytesplash-dark flex items-center gap-2"
                        disabled={isSubmitting || true}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin mr-1 h-4 w-4 border-2 border-white border-opacity-50 border-t-transparent rounded-full" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            Send Message(Under Progress)
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </motion.div>

        <motion.div
          className="space-y-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Card className="shadow-soft">
              <CardHeader className="pb-3">
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Ways to reach our support team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-10 h-10 rounded-full bg-bytesplash-light flex items-center justify-center text-bytesplash-primary">
                    <Mail size={18} />
                  </div>
                  <div>
                    <h3 className="font-medium text-bytesplash-gray-800 text-left">Email</h3>
                    <p className="text-bytesplash-gray-600">enquires@bytesplash.in</p>
                    <p className="text-bytesplash-gray-500 text-sm">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1 w-10 h-10 rounded-full bg-bytesplash-light flex items-center justify-center text-bytesplash-primary">
                    <Phone size={18} />
                  </div>
                  <div>
                    <h3 className="font-medium text-bytesplash-gray-800">Phone</h3>
                    <p className="text-bytesplash-gray-600">

                      <a href="callto:+916301062858">+91 (630) 106-2858</a>
                    </p>
                    <p className="text-bytesplash-gray-500 text-sm">Mon-Fri from 9am to 6pm</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-1 w-10 h-10 rounded-full bg-bytesplash-light flex items-center justify-center text-bytesplash-primary">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <h3 className="font-medium text-bytesplash-gray-800">Office</h3>
                    <p className="text-bytesplash-gray-600">4th Floor , GR Arcade, PR Layout, Opposite Aishwarya Oppulence Apartments</p>
                    <p className="text-bytesplash-gray-500 text-sm">Marathahalli Outer Ringroad , Marathahalli, Bengaluru, KA 560037</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="shadow-soft">
              <CardHeader className="pb-3">
                <CardTitle>Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-bytesplash-light/30 p-3 rounded-lg">
                  <h4 className="font-medium text-bytesplash-primary">How do I reset my password?</h4>
                  <p className="text-sm text-bytesplash-gray-600 mt-1">Visit the login page and click on "Forgot Password" to receive reset instructions.</p>
                </div>

                <div className="bg-bytesplash-light/30 p-3 rounded-lg">
                  <h4 className="font-medium text-bytesplash-primary">Can I access courses offline?</h4>
                  <p className="text-sm text-bytesplash-gray-600 mt-1">Yes, you can download course materials for offline viewing through our mobile app.</p>
                </div>

                <div className="bg-bytesplash-light/30 p-3 rounded-lg">
                  <h4 className="font-medium text-bytesplash-primary">How do I get a certificate?</h4>
                  <p className="text-sm text-bytesplash-gray-600 mt-1">Certificates are issued automatically upon successful completion of all course requirements.</p>
                </div>

                <div className="text-center mt-2">
                  <Button variant="link" className="text-bytesplash-primary">
                    View All FAQs
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUs;
